import React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import {
  generateBidApprovalNotification,
  generateFollowUpReminder,
  generateOnboardingTask,
} from "./workflow-templates";

interface QuickAutomationModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: string;
  recordId: string;
}

export function QuickAutomationModal({ isOpen, onClose, context, recordId }: QuickAutomationModalProps) {
  const navigate = useNavigate();
  const createWorkflow = useMutation(api.workflows.create);

  const handleCreateTemplate = async (
    title: string,
    generatorFn: (id: string) => { nodes: any[]; edges: any[] }
  ) => {
    try {
      const { nodes, edges } = generatorFn(recordId);
      
      const newId = await createWorkflow({
        name: title,
        nodes,
        edges,
      });
      
      toast.success("Automation created successfully!");
      onClose();
      // Optionally redirect them to see the created workflow, or just let them stay on the page.
      // navigate(`/workflows/builder?id=${newId}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to create automation.");
    }
  };

  const handleOpenAdvanced = () => {
    onClose();
    navigate(`/workflows/builder?context=${context}&recordId=${recordId}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Automation</DialogTitle>
          <DialogDescription>
            Choose a quick automation template, or build your own from scratch.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-4">
          <Button
            variant="outline"
            className="flex items-center justify-start h-auto p-4 gap-4 hover:border-primary hover:bg-primary/5 transition-all"
            onClick={() => handleCreateTemplate("Notify me when approved", generateBidApprovalNotification)}
          >
            <div className="bg-blue-100 p-2 rounded-full text-blue-600">
              <Mail className="h-5 w-5" />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-gray-900">Notify me when approved</div>
              <div className="text-xs text-gray-500 font-normal mt-0.5">Sends an email immediately upon approval.</div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="flex items-center justify-start h-auto p-4 gap-4 hover:border-primary hover:bg-primary/5 transition-all"
            onClick={() => handleCreateTemplate("3-Day Follow Up Reminder", generateFollowUpReminder)}
          >
            <div className="bg-orange-100 p-2 rounded-full text-orange-600">
              <Clock className="h-5 w-5" />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-gray-900">3-Day Follow Up Reminder</div>
              <div className="text-xs text-gray-500 font-normal mt-0.5">Creates a task 3 days after submission.</div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="flex items-center justify-start h-auto p-4 gap-4 hover:border-primary hover:bg-primary/5 transition-all"
            onClick={() => handleCreateTemplate("Onboarding Task on Signature", generateOnboardingTask)}
          >
            <div className="bg-green-100 p-2 rounded-full text-green-600">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-gray-900">Create onboarding task</div>
              <div className="text-xs text-gray-500 font-normal mt-0.5">Creates a team task when converted to client.</div>
            </div>
          </Button>
        </div>

        <div className="flex flex-col border-t pt-4 mt-2">
          <Button 
            variant="ghost" 
            className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 w-full justify-center"
            onClick={handleOpenAdvanced}
          >
            Need something custom? Open Advanced Builder <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
